import type { Interaction, NormalizedRun, TranscriptEvent } from '../api/types'

export type VoxelAvatarState =
  | 'idle'
  | 'working'
  | 'waiting'
  | 'alarmed'
  | 'celebrating'
  | 'slumped'

export interface LastToolCall {
  id: string
  name: string
}

/** Tool activity counts as "working" for this long after the last tool event. */
export const WORKING_WINDOW_MS = 30_000
/** An alarmed character stays alarmed at least this long (posture dwell). */
export const ALARM_DWELL_MS = 15_000

function isAlarmEvent(e: TranscriptEvent): boolean {
  return (
    e.type === 'guardrail_violation' ||
    e.type === 'guardrail_violations' ||
    e.type === 'rework' ||
    e.type === 'error'
  )
}

function ageMs(e: TranscriptEvent, now: number): number {
  const t = Date.parse(e.timestamp ?? '')
  // An unparseable timestamp must degrade to "ancient", never to "brand new":
  // treating it as age 0 would latch the run in a posture-changing state
  // (alarmed stands the character up) for as long as the event is buffered.
  return Number.isFinite(t) ? now - t : Number.POSITIVE_INFINITY
}

export function computeAvatarState(
  run: NormalizedRun,
  interactions: Interaction[],
  transcripts: TranscriptEvent[],
  now: number = Date.now(),
): VoxelAvatarState {
  if (run.status === 'completed') return 'celebrating'
  if (run.status === 'failed') return 'slumped'
  if (interactions.some((i) => !i.respondedAt)) return 'waiting'

  // Time-based, not count-based: a fixed 10-event window let a tool call
  // scroll out purely because newer chatter arrived, flipping the character
  // between postures (and back) with no real change in the run.
  // Reverse-scan in place: this runs per-run on every streamed token, so
  // allocating two reversed copies each time is real waste.
  let alarm: TranscriptEvent | undefined
  let tool: TranscriptEvent | undefined
  for (let i = transcripts.length - 1; i >= 0; i--) {
    const e = transcripts[i]
    if (!alarm && isAlarmEvent(e)) alarm = e
    if (!tool && (e.type === 'tool_call' || e.type === 'tool_info')) tool = e
    if (alarm && tool) break
  }
  if (alarm && ageMs(alarm, now) <= ALARM_DWELL_MS) return 'alarmed'
  if (tool && ageMs(tool, now) <= WORKING_WINDOW_MS) return 'working'

  if (run.phases.some((p) => p.status === 'running')) return 'working'
  return 'idle'
}

export function extractLastToolCall(transcripts: TranscriptEvent[]): LastToolCall | null {
  for (let i = transcripts.length - 1; i >= 0; i--) {
    const e = transcripts[i]
    if (e.type !== 'tool_call' && e.type !== 'tool_info') continue
    try {
      const parsed = JSON.parse(e.content) as { name?: string }
      if (parsed.name) return { id: e.id, name: parsed.name }
    } catch {
      // not JSON — skip
    }
  }
  return null
}
